import React, { useMemo, useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { Check, ChevronDown, LogOut, UserPen, Plus, Menu, X as XIcon } from "lucide-react";
import { toast } from "react-hot-toast";


import clubbieLogo from "../../assets/images/clubbie_logo_full.svg";
import TournamentBadge from "../../components/TournamentBadge/TournamentBadge";
import {
  fetchOrganisations,
  getSelectedOrganisation,
  selectOrganisation,
  updateSelectedOrganisation,
  resetTournamentState as resetOrgTournamentState,
} from "../../redux/slices/organisationSlice";
import {
  fetchTournaments,
  clearAllSelections,
  selectTournament,
  resetTournamentState,
  updateDefaultSelection,
  respondToMemberInvite,
} from "../../redux/slices/tournamentSlice";
import { ReactComponent as HomeIcon } from "../../assets/images/icons/home_icon.svg";
import { ReactComponent as FixturesIcon } from "../../assets/images/icons/fixtures.svg";
import { ReactComponent as MembersIcon } from "../../assets/images/icons/participants.svg";
import { ReactComponent as PaymentsIcon } from "../../assets/images/icons/payments.svg";
import { ReactComponent as ResultsIcon } from "../../assets/images/icons/results.svg";
import { ReactComponent as StandingsIcon } from "../../assets/images/icons/standings.svg";
import { ReactComponent as StatisticsIcon } from "../../assets/images/icons/statistics.svg";
import { ReactComponent as FormatsIcon } from "../../assets/images/icons/formats.svg";
import NotificationsIcon from "../../assets/images/icons/notifications.svg";
import { ReactComponent as CompetitionsIcon } from "../../assets/images/icons/competitions.svg";
import OrganisationMembersIcon from "../../components/Icons/OrganisationMembersIcon";
import SettingsIcon from "../../components/Icons/SettingsIcon";
import { logoutUser } from "../../redux/slices/authSlice";
import { AUTH_STACK, DASHBOARD_STACK } from "../../utils/screenNames";
import sidebarConfig from "../../utils/sidebarConfig";
import useTournamentRole from "../../hooks/useTournamentRole";
import EditProfileModal from "../../components/EditProfileModal/EditProfileModal";

const notificationFilter = "brightness(0) saturate(100%) invert(69%) sepia(11%) saturate(346%) hue-rotate(181deg) brightness(95%) contrast(90%)";

const DashboardLayout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const profileButtonRef = useRef(null);
  const dropdownRef = useRef(null);
  const topbarDropdownRef = useRef(null);

  const {
    organisations,
    selectedOrganisation,
    loading: orgLoading,
    totalPages: orgTotalPages,
    page: orgCurrentPage,
  } = useSelector((state) => state.organisation);

  const {
    tournaments,
    selectedTournament,
    selectedCategory,
    loading: tournamentLoading,
    totalPages: tournamentTotalPages,
  } = useSelector((state) => state.tournament);



  const sport = selectedTournament?.sport;
  const tournamentLogo = selectedTournament?.logo;

  const user = useSelector((state) => state.user);
  const { role: tournamentRole, permissions: tournamentPermissions } = useTournamentRole();
  const visibleSidebarItems = (sidebarConfig?.upper || []).filter(
    (item) => !item.requires || tournamentPermissions?.[item.requires]
  );

  // Fix: Add null checks for user data
  const profileEmail = user?.user?.local?.email || '';
  const profileInitial = profileEmail ? profileEmail.charAt(0).toUpperCase() : '';

  const profileImage = user?.user?.profile?.image;
  const profileName = user?.user?.profile?.fullName || '';

  const getUserRoleInTournament = (tournament) => {
    const currentUserId = user?.user?.id;
    if (!tournament || !currentUserId) return 'Member';
    const ownerId = tournament.ownerId?.id || tournament.ownerId?._id || tournament.ownerId;
    if (ownerId?.toString() === currentUserId.toString()) return 'Owner';
    const member = tournament.members?.find(m => {
      const memberId = m.userId?.id || m.userId?._id || m.userId;
      return memberId?.toString() === currentUserId.toString();
    });
    if (member?.role === 'tournamentAdmin') return 'Admin';
    if (member?.role === 'referee') return 'Referee';
    if (member?.role) return member.role.charAt(0).toUpperCase() + member.role.slice(1);
    return 'Member';
  };

  const currentUserId = user?.user?.id;

  const pendingInvitations = useMemo(() => {
    if (!tournaments || !currentUserId) return [];
    return tournaments.filter((t) =>
      t.members?.some((m) => {
        const memberId = m.userId?.id || m.userId?._id || m.userId;
        return memberId?.toString() === currentUserId.toString() && m.status === 'PENDING';
      })
    );
  }, [tournaments, currentUserId]);

  const getMemberRole = (tournament) => {
    if (!currentUserId) return '';
    const member = tournament.members?.find((m) => {
      const memberId = m.userId?.id || m.userId?._id || m.userId;
      return memberId?.toString() === currentUserId.toString();
    });
    if (member?.role === 'tournamentAdmin') return 'Admin';
    if (member?.role === 'referee') return 'Referee';
    return member?.role?.charAt(0).toUpperCase() + member?.role?.slice(1) || 'Member';
  };

  const handleInviteResponse = async (tournamentId, response) => {
    try {
      setRespondingTo(`${tournamentId}-${response}`);
      await dispatch(respondToMemberInvite({ tournamentId, response })).unwrap();
      toast.success(response === 'accept' ? 'Invitation accepted!' : 'Invitation rejected');
      // Refetch tournaments to get updated member status
      if (selectedOrganisation?.id) {
        dispatch(fetchTournaments({ organisationId: selectedOrganisation.id }));
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to respond to invitation');
    } finally {
      setRespondingTo(null);
    }
  };

  const [tournamentPage, setTournamentPage] = useState(1);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [showTopbarDropdown, setShowTopbarDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const [hasFetchedTournaments, setHasFetchedTournaments] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const notificationRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch initial data
  useEffect(() => {
    (async () => {
      try {
        await dispatch(fetchOrganisations({}));
        await dispatch(getSelectedOrganisation());
      } catch (error) {
      }
    })();
  }, [dispatch]);

  // Fetch tournaments when organization changes
  useEffect(() => {
    if (selectedOrganisation?.id) {
      setHasFetchedTournaments(false);
      dispatch(fetchTournaments({ organisationId: selectedOrganisation.id }))
        .unwrap()
        .then(() => setHasFetchedTournaments(true))
        .catch((error) => {
          setHasFetchedTournaments(true);
          if (error?.message?.includes('Access denied') ||
              error?.status === 403) {
            dispatch(selectOrganisation(null));
          }
        });
    }
  }, [selectedOrganisation, dispatch]);

  // Redirect if profile is incomplete or no competitions exist
  const profile = user?.user?.profile;
  const hasIncompleteProfile =
    profile && (!profile.fullName || !profile.dateOfBirth || !profile.gender || !profile.country);

  // Routes that should never be redirected away from
  const isExemptRoute =
    location.pathname === AUTH_STACK.PERSONAL_DETAILS ||
    location.pathname === AUTH_STACK.LOCATION_DETAILS ||
    location.pathname === DASHBOARD_STACK.COMPETITIONS ||
    location.pathname === DASHBOARD_STACK.COMPETITIONS_CONFIG ||
    location.pathname === DASHBOARD_STACK.COMPETITIONS_VENUE ||
    location.pathname === DASHBOARD_STACK.COMPETITIONS_FEE;

  useEffect(() => {
    if (!profile || isExemptRoute) return;

    if (hasIncompleteProfile) {
      navigate(AUTH_STACK.PERSONAL_DETAILS, { replace: true });
    }
  }, [profile, hasIncompleteProfile, isExemptRoute, navigate]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (topbarDropdownRef.current && !topbarDropdownRef.current.contains(event.target)) {
        setShowTopbarDropdown(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    }; 

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    }; 
  }, []); 

  // Modified handlers to save selections to database
  const handleOrgChange = async (org) => {
    // Clear all tournament-related selections first
    dispatch(clearAllSelections());
    // Select the new organization
    dispatch(selectOrganisation(org));

    // Save organization selection to database
    try {
      await dispatch(updateSelectedOrganisation({ id: org.id })).unwrap();
    } catch (error) {
    }
  };

  const handleTournamentChange = async (tournament) => {
    dispatch(selectTournament(tournament));
    setIsDropdownOpen(false); // Close dropdown after selection

    // Save tournament selection to database
    try {
      await dispatch(
        updateDefaultSelection({
          organisationId: selectedOrganisation.id,
          tournamentId: tournament.id,
        })
      ).unwrap();
    } catch (error) {
    }
  };

  const handleLoadMoreTournaments = () => {
    if (selectedOrganisation?.id && tournamentPage < tournamentTotalPages) {
      const nextPage = tournamentPage + 1;
      dispatch(
        fetchTournaments({
          organisationId: selectedOrganisation.id,
          page: nextPage,
          append: true,
        })
      );
      setTournamentPage(nextPage);
    }
  };

  const getIconForOption = useMemo(
    () => ({
      home_icon: <HomeIcon />,
      competitions_icon: <CompetitionsIcon />,
      members_icon: <MembersIcon />,
      fixtures_icon: <FixturesIcon />,
      payments_icon: <PaymentsIcon />,
      results_icon: <ResultsIcon />,
      statistics_icon: <StatisticsIcon />,
      standings_icon: <StandingsIcon />,
      formats_icon: <FormatsIcon />,
      organisation_members_icon: <OrganisationMembersIcon />,
      // notifications_icon: <NotificationsIconSvg />,
      settings_icon: <SettingsIcon />,
    }),
    []
  );

  const handleLogout = () => {
    // Clear all Redux state first to prevent data leakage
    dispatch(clearAllSelections());
    dispatch(resetTournamentState());
    dispatch(resetOrgTournamentState());
    
    dispatch(logoutUser())
      .unwrap()
      .then(() => {
        navigate(AUTH_STACK.LOGIN_OPTIONS, { replace: true });
      })
      .catch((error) => {
        // Still navigate even if API call fails
        navigate(AUTH_STACK.LOGIN_OPTIONS, { replace: true });
      });
  };

  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const handleEditProfile = () => {
    setShowTopbarDropdown(false);
    setIsEditProfileModalOpen(true);
  };

  const toggleTopbarDropdown = () => {
    setShowTopbarDropdown(!showTopbarDropdown);
  };

  const hideSidebar = location.pathname.startsWith('/competition');

  return (
    <>
    <div className="flex flex-col h-screen bg-app-bg text-[#FEFCEB] max-w-[1512px] mx-auto w-full">
      {/* Topbar - Full Width */}
      <div className="w-full flex justify-between items-center px-3 py-3 md:px-6 md:py-4 border-b border-[#0D1B29] bg-[#000F1F] z-[100]">
        <div className="flex items-center gap-3">
          <button
            className="md:hidden p-1 text-[#FEFCEB] hover:text-[#00E7FF] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <XIcon size={22} /> : <Menu size={22} />}
          </button>
          <img
            className="h-auto w-[100px] md:w-[150px] cursor-pointer"
            src={clubbieLogo}
            alt="clubbie logo"
            onClick={() => navigate('/')}
          />
        </div>

        <div className="flex items-center gap-4">
          <div className="flex relative items-center" ref={notificationRef}>
            <button
              className="relative cursor-pointer bg-transparent border-none p-0"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <img
                className="w-5 h-5 transition-[filter] duration-200 hover:brightness-0 hover:invert"
                style={{ filter: notificationFilter }}
                src={NotificationsIcon}
                alt="notifications"
              />
              {pendingInvitations.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] rounded-full bg-[#00E7FF] text-[#021529] text-[10px] font-bold flex items-center justify-center px-1">
                  {pendingInvitations.length}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute top-full right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-app-bg border border-app-border rounded-2xl shadow-xl overflow-hidden z-[1000]">
                <div className="px-4 py-3 border-b border-app-border">
                  <p className="text-sm font-medium text-app-text">Invitations</p>
                </div>

                <div className="max-h-[300px] overflow-y-auto">
                  {pendingInvitations.length === 0 ? (
                    <div className="px-4 py-6 text-center text-app-text-secondary text-sm">
                      No pending invitations
                    </div>
                  ) : (
                    pendingInvitations.map((tournament) => (
                      <div
                        key={tournament.id}
                        className="px-4 py-3 border-b border-app-border last:border-b-0"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 rounded-lg shrink-0 overflow-hidden">
                            <TournamentBadge src={tournament.logo} alt={tournament.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-app-text truncate">{tournament.name}</p>
                            <p className="text-xs text-app-text-secondary">Invited as {getMemberRole(tournament)}</p>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleInviteResponse(tournament.id, 'reject')}
                            disabled={!!respondingTo}
                            className="flex-1 bg-app-error hover:bg-red-600 text-blue-shade-8 font-medium py-1.5 rounded-2xl text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                          >
                            {respondingTo === `${tournament.id}-reject` ? 'Rejecting...' : 'Reject'}
                          </button>
                          <button
                            onClick={() => handleInviteResponse(tournament.id, 'accept')}
                            disabled={!!respondingTo}
                            className="flex-1 bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium py-1.5 rounded-2xl text-xs transition-colors disabled:opacity-50 cursor-pointer"
                          >
                            {respondingTo === `${tournament.id}-accept` ? 'Accepting...' : 'Accept'}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={topbarDropdownRef}>
            <button
              className="w-9 h-9 rounded-full overflow-hidden border-2 border-app-border cursor-pointer bg-app-surface flex items-center justify-center"
              onClick={toggleTopbarDropdown}
            >
              {profileImage ? (
                <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center font-semibold text-sm text-[#FEFCEB]">
                  {profileInitial}
                </div>
              )}
            </button>

            {showTopbarDropdown && (
              <div className="absolute top-full right-0 mt-2 w-64 max-w-[calc(100vw-2rem)] bg-app-bg border border-app-border rounded-2xl shadow-xl overflow-hidden z-[1000]">
                {/* User info */}
                <div className="px-4 py-3 border-b border-app-border">
                  <p className="text-sm font-medium text-app-text truncate">{profileName}</p>
                  <p className="text-xs text-app-text-secondary truncate">{profileEmail}</p>
                </div>

                {/* Actions */}
                <div className="py-0">
                  <button
                    className="w-full text-left px-4 py-3 text-sm text-app-text hover:bg-app-hover transition-colors flex items-center gap-3 cursor-pointer"
                    onClick={handleEditProfile}
                  >
                    <UserPen size={16} className="text-app-text-secondary" />
                    Edit profile
                  </button>
                </div>

                {/* Divider + Logout */}
                <div className="border-t border-app-border">
                  <button
                    className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-3 cursor-pointer"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {!hideSidebar && isMobileMenuOpen && (
        <div className="fixed inset-0 z-[200] flex md:hidden">
          <div className="w-[280px] bg-[#000F1F] flex flex-col h-full border-r border-[#0D1B29] overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {/* Tournament Dropdown */}
            <div className="relative mx-4 my-1" ref={dropdownRef}>
              <div
                className="flex items-center gap-2 p-2 mt-2 bg-transparent border-none rounded-xl cursor-pointer transition-all duration-200"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <div className="w-8 h-8 rounded-lg shrink-0 overflow-hidden">
                  <TournamentBadge src={tournamentLogo} alt="comp logo" className="w-full h-full object-cover" />
                </div>

                <div className="flex-1 flex flex-col">
                  <span className="text-base font-medium text-[14px] text-[#FEFCEB] leading-tight text-s">
                    {selectedTournament?.name || "No available competitions"}
                  </span>
                  <span className="text-xs text-[#7F8585] leading-tight">
                    {tournamentRole ? tournamentRole.charAt(0).toUpperCase() + tournamentRole.slice(1) : ''}
                  </span>
                </div>

                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 text-[#a1b5d8] shrink-0 ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
              </div>

              {isDropdownOpen && (
                <div className="absolute top-full w-full left-0 right-0 bg-transparent border-none rounded-none z-[1000] max-h-none mt-1 overflow-y-auto">
                  <div className="bg-[#000F1F] border border-[#192634] rounded-[12px] shadow-lg overflow-hidden flex flex-col">
                    <div className="max-h-[300px] overflow-y-auto flex-1 min-h-0 bg-transparent [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#7F8585] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-[#9CA3AF]">
                      {tournamentLoading ? (
                        <div className="px-3 py-2 text-[#7F8585] text-sm">Loading tournaments...</div>
                      ) : tournaments?.length > 0 ? (
                        <>
                          {[...tournaments].filter((t) => {
                            const isPending = t.members?.some((m) => {
                              const memberId = m.userId?.id || m.userId?._id || m.userId;
                              return memberId?.toString() === currentUserId?.toString() && m.status === 'PENDING';
                            });
                            return !isPending;
                          }).sort((a, b) => {
                            if (a.id === selectedTournament?.id) return -1;
                            if (b.id === selectedTournament?.id) return 1;
                            return 0;
                          }).map((tournament, index) => {
                            const isSelected = selectedTournament?.id === tournament.id;
                            return (
                              <div
                                key={tournament.id}
                                className={`flex px-3 py-3 justify-between items-center cursor-pointer transition-colors hover:bg-[#0D1B29] ${index < tournaments.length - 1 ? 'border-b border-[#192634]' : ''}`}
                                onClick={() => handleTournamentChange(tournament)}
                              >
                                <div className="flex items-center gap-2 overflow-hidden flex-1 min-w-0">
                                  <div className="w-8 h-8 rounded-[6.4px] shrink-0 overflow-hidden">
                                    <TournamentBadge src={tournament.logo} alt={tournament.name} className="w-full h-full object-cover" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className={`text-sm font-medium truncate ${isSelected ? 'text-[#00E7FF]' : 'text-[#E5E5D6]'}`}>{tournament.name}</div>
                                    <div className="text-[12px] font-normal text-[#7F8585]">{getUserRoleInTournament(tournament)}</div>
                                  </div>
                                </div>
                                {isSelected && <Check size={16} className="shrink-0 text-[#00E7FF] ml-2" />}
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <div className="px-3 py-2 text-[#7F8585] text-sm text-left">No tournaments available</div>
                      )}
                    </div>
                    <div className="border-t border-[#192634] hover:bg-app-hover">
                      <button
                        onClick={() => { navigate(DASHBOARD_STACK.COMPETITIONS); setIsDropdownOpen(false); }}
                        className="w-full flex items-center gap-1 px-4 py-0 h-[44px] text-[#FEFCEB] bg-transparent border-none cursor-pointer text-[16px] font-semibold transition-colors text-left hover:text-[#00E7FF] rounded-[8px]"
                      >
                        <Plus size={18} className="shrink-0" />
                        <span>Add competition</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Menu Items */}
            <div className="flex-1 flex flex-col overflow-y-auto mx-3 mt-2 [&::-webkit-scrollbar]:hidden">
              <div className="flex-1">
                {visibleSidebarItems.map((item, index) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <div
                      key={index}
                      className={`w-full h-10 flex px-3 items-center gap-2 cursor-pointer text-[14px] font-normal my-0.5 [&_svg]:w-5 [&_svg]:h-[18px] [&_svg]:shrink-0 ${
                        isActive
                          ? 'bg-[#001A2A] text-[#00E7FF] rounded-full [&_svg_path]:stroke-[#00E7FF] [&_svg_circle]:stroke-[#00E7FF] [&_svg_rect]:stroke-[#00E7FF]'
                          : 'text-[#7F8585] rounded-lg hover:bg-[#001A2A] hover:text-[#00E7FF] hover:rounded-full [&:hover_svg_path]:stroke-[#00E7FF] [&:hover_svg_circle]:stroke-[#00E7FF] [&:hover_svg_rect]:stroke-[#00E7FF]'
                      }`}
                      onClick={() => navigate(item.path)}
                    >
                      {getIconForOption[item.icon]}
                      <span>{item.title}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Backdrop */}
          <div className="flex-1 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
        </div>
      )}

      {/* Bottom Section with Sidebar and Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Desktop only */}
        {!hideSidebar && <div className="hidden md:flex w-[280px] border-r border-[#0D1B29] bg-[#000F1F] flex-col [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">

          {/* Tournament Dropdown */}
          <div className="relative mx-4 my-1" ref={dropdownRef}>
            <div
              className="flex items-center gap-2 p-2 mt-2 bg-transparent border-none rounded-xl cursor-pointer transition-all duration-200"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <div className="w-8 h-8 rounded-lg shrink-0 overflow-hidden">
                <TournamentBadge src={tournamentLogo} alt="comp logo" className="w-full h-full object-cover" />
              </div>

              <div className="flex-1 flex flex-col">
                <span className="text-base text-[14px] font-medium text-[#FEFCEB] leading-tight text-s">
                  {selectedTournament?.name || "No available competitions"}
                </span>
                <span className="text-xs text-[#7F8585] leading-tight">
                  {tournamentRole ? tournamentRole.charAt(0).toUpperCase() + tournamentRole.slice(1) : ''}
                </span>
              </div>

              <ChevronDown
                size={16}
                className={`transition-transform duration-200 text-[#a1b5d8] shrink-0 ${isDropdownOpen ? 'rotate-180' : ''}`}
              />
            </div>

            {isDropdownOpen && (
              <div className="absolute top-full w-full md:w-[330px] left-0 right-0 bg-transparent border-none rounded-none z-[1000] max-h-none mt-1 overflow-y-auto">
                {/* Current competitions box */}
                <div className="bg-[#000F1F] border border-[#192634] rounded-[12px] shadow-lg overflow-hidden flex flex-col">
                  {/* {tournaments?.length > 0 && <div className="px-[12px] mt-[6px] text-[#7F8585] text-[13px] font-normal shrink-0">
                    Current competitions
                  </div>} */}

                  {/* Scrollable List Container */}
                  <div className="max-h-[300px] overflow-y-auto flex-1 min-h-0 bg-transparent [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#7F8585] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-[#9CA3AF]">
                    {tournamentLoading ? (
                      <div className="px-3 py-2 text-[#7F8585] text-sm">
                        Loading tournaments...
                      </div>
                    ) : tournaments?.length > 0 ? (
                      <>
                        {[...tournaments].filter((t) => {
                          // Hide tournaments where user has a pending invitation
                          const isPending = t.members?.some((m) => {
                            const memberId = m.userId?.id || m.userId?._id || m.userId;
                            return memberId?.toString() === currentUserId?.toString() && m.status === 'PENDING';
                          });
                          return !isPending;
                        }).sort((a, b) => {
                          if (a.id === selectedTournament?.id) return -1;
                          if (b.id === selectedTournament?.id) return 1;
                          return 0;
                        }).map((tournament, index) => {
                          const isSelected = selectedTournament?.id === tournament.id;
                          return (
                            <div
                              key={tournament.id}
                              className={`flex px-3 py-3 justify-between items-center cursor-pointer transition-colors hover:bg-[#0D1B29] 
                              ${
                                index < tournaments.length - 1 ? 'border-b border-[#192634]' : ''
                              }`}
                              onClick={() => handleTournamentChange(tournament)}
                            >
                              <div className="flex items-center gap-2 overflow-hidden flex-1 min-w-0">
                                {/* Tournament Logo/Placeholder */}
                                <div className="w-8 h-8 rounded-[6.4px] shrink-0 overflow-hidden">
                                  <TournamentBadge src={tournament.logo} alt={tournament.name} className="w-full h-full object-cover" />
                                </div>

                                {/* Name & Status */}
                                <div className="min-w-0 flex-1">
                                  <div className={`text-sm font-medium truncate ${isSelected ? 'text-[#00E7FF]' : 'text-[#E5E5D6]'}`}>
                                    {tournament.name}
                                  </div>
                                  <div className="text-[12px] font-normal text-[#7F8585]">
                                    {getUserRoleInTournament(tournament)}
                                  </div>
                                </div>
                              </div>

                              {/* Checkmark for Selected */}
                              {isSelected && (
                                <Check size={16} className="shrink-0 text-[#00E7FF] ml-2" />
                              )}
                            </div>
                          );
                        })}
                        {tournamentPage < tournamentTotalPages && (
                          <div
                            className="px-3 py-2 text-center text-[#00E7FF] text-sm font-medium cursor-pointer rounded transition-colors hover:bg-white/5"
                            onClick={handleLoadMoreTournaments}
                          >
                            Load More...
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="px-3 py-2 text-[#7F8585] text-sm text-left">
                        No tournaments available
                      </div>
                    )}
                  </div>

                  {/* Add Competition Button */}
                  <div className="border-t border-[#192634] hover:bg-app-hover">
                    <button
                      onClick={() => {
                        navigate(DASHBOARD_STACK.COMPETITIONS);
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-1 px-4 py-0 h-[44px] text-[#FEFCEB] bg-transparent border-none cursor-pointer text-[14px] font-medium transition-colors text-left hover:text-[#00E7FF] rounded-[8px]"
                    >
                      <Plus size={18} className="shrink-0" />
                      <span>Create new competition</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div className="flex-1 flex flex-col overflow-y-auto mx-3 mt-2 [&::-webkit-scrollbar]:hidden">
            <div className="flex-1">
              {visibleSidebarItems.map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <div
                    key={index}
                    className={`w-full h-10 flex px-3 items-center gap-2 cursor-pointer text-[14px] font-normal my-0.5 [&_svg]:w-5 [&_svg]:h-[18px] [&_svg]:shrink-0 ${
                      isActive
                        ? 'bg-[#001A2A] text-[#00E7FF] rounded-full [&_svg_path]:stroke-[#00E7FF] [&_svg_circle]:stroke-[#00E7FF] [&_svg_rect]:stroke-[#00E7FF]'
                        : 'text-[#7F8585] rounded-lg hover:bg-[#001A2A] hover:text-[#00E7FF] hover:rounded-full [&:hover_svg_path]:stroke-[#00E7FF] [&:hover_svg_circle]:stroke-[#00E7FF] [&:hover_svg_rect]:stroke-[#00E7FF]'
                    }`}
                    onClick={() => navigate(item.path)}
                  >
                    {getIconForOption[item.icon]}
                    <span>{item.title}</span>
                  </div>
                );
              })}
            </div>

          </div>
        </div>}

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 bg-[#000F1F] overflow-y-auto relative [&::-webkit-scrollbar]:hidden">
          <Outlet />
        </div>
      </div>
    </div>
    <EditProfileModal
      isOpen={isEditProfileModalOpen}
      onClose={() => setIsEditProfileModalOpen(false)}
    />
    </>
  );
};

export default DashboardLayout;